// SKU API Service
// ใช้สำหรับเรียก API endpoints ของ SKU

import { AxiosResponse } from 'axios';
import httpClient from '../axios/interceptors';
import { handleResponseError } from 'helpers/utils';
import { API } from './endpoint';
import { UpdateProductSkuDto, UpdateProductSkuResponse } from './interface';

// Types
export interface CreateSkuRequest {
  product_id?: string;
  variant_id?: string;
  sku_value: string;
  img_url?: string;
  product_capacity?: number;
  product_quantity?: number;
  lead_time?: number;
  code?: string;
  width?: number;
  length?: number;
  height?: number;
  weight?: number;
  is_active?: boolean;
  factory_price?: number;
  wholesale_price?: number;
  retail_price?: number;
  vip_price?: number;
  base_factory_price?: number;
  base_wholesale_price?: number;
  base_retail_price?: number;
  transport_price?: number;
  percent_payment_gateway?: number;
}

export interface UpdateSkuRequest {
  sku_value?: string;
  img_url?: string;
  product_capacity?: number;
  product_quantity?: number;
  lead_time?: number;
  code?: string;
  width?: number;
  length?: number;
  height?: number;
  weight?: number;
  is_active?: boolean;
  factory_price?: number;
  wholesale_price?: number;
  retail_price?: number;
  vip_price?: number;
  base_factory_price?: number;
  base_wholesale_price?: number;
  base_retail_price?: number;
  transport_price?: number;
  percent_payment_gateway?: number;
}

export interface SkuResponse {
  id: string;
  variant_id: string;
  sku_value: string;
  img_url?: string;
  product_capacity: number;
  product_quantity: number;
  lead_time: number;
  code?: string;
  width: number;
  length: number;
  height: number;
  weight: number;
  is_active: boolean;
  factory_price: number;
  wholesale_price: number;
  retail_price: number;
  vip_price: number;
  base_factory_price: number;
  base_wholesale_price: number;
  base_retail_price: number;
  transport_price: number;
  percent_payment_gateway: number;
  product_id?: string;
  create_id: string;
  create_date: string;
  update_id?: string;
  update_date?: string;
  is_delete: boolean;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export interface ApiError {
  success: false;
  message: string;
  errors?: string[];
}

export interface UpdateProductSkuRequest {
  product_id: string;
  variants: Array<{
    id: string;
    name: string;
    capacity: number;
    items: Array<{
      id: string;
      name: string;
      product_capacity: number;
      product_quantity: number;
      lead_time: number;
      product_code: string;
      image_id: string;
      image_url: string;
      width: number;
      length: number;
      height: number;
      weight: number;
      is_active: boolean;
      is_draft: boolean;
    }>;
  }>;
  prices: Array<{
    id: string;
    variant_1_item_id: string;
    variant_2_item_id: string | null;
    factory_price: number;
    wholesale_price: number;
    retail_price: number;
    vip_price: number;
    base_factory_price: number;
    base_vip_price: number;
    base_wholesale_price: number;
    base_retail_price: number;
    transport_price: number;
    percent_payment_gateway: number;
    payment_getway: number;
    commistion_lv1: number;
    commistion_lv2: number;
    commistion_lv3: number;
    commistion_lv4: number;
    commistion_lv5: number;
    total_commission: number;
    unit_factory_price: string;
    unit_wholesale_price: string;
    unit_retail_price: string;
    unit_vip_price: string;
  }>;
}

// API Functions

/**
 * สร้าง SKU ใหม่
 * @param data - ข้อมูล SKU ที่ต้องการสร้าง
 * @returns Promise<SkuResponse>
 */
export const createSku = async (data: CreateSkuRequest): Promise<SkuResponse> => {
  try {
    const response: AxiosResponse<ApiResponse<SkuResponse>> = await httpClient
      .post('/product/sku', data)
      .catch((error) => handleResponseError(error));

    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'ไม่สามารถสร้าง SKU ได้');
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('เกิดข้อผิดพลาดในการสร้าง SKU');
    }
  }
};

/**
 * อัปเดต SKU
 * @param skuId - SKU ID
 * @param data - ข้อมูล SKU ที่ต้องการอัปเดต
 * @returns Promise<SkuResponse>
 */
export const updateSku = async (skuId: string, data: UpdateSkuRequest): Promise<SkuResponse> => {
  try {
    const response: AxiosResponse<ApiResponse<SkuResponse>> = await httpClient
      // หมายเหตุ: httpClient ใช้ axios instance ดังนั้นเรียก put ได้
      .put(`/product/sku/${skuId}`, data)
      .catch((error) => handleResponseError(error));

    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'ไม่สามารถอัปเดต SKU ได้');
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('เกิดข้อผิดพลาดในการอัปเดต SKU');
    }
  }
};

/**
 * ลบ SKU
 * @param skuId - SKU ID
 * @returns Promise<void>
 */
export const deleteSku = async (skuId: string): Promise<void> => {
  try {
    const response: AxiosResponse<{ status?: string; data?: string; message?: string }> = await httpClient
      .delete(`/product/sku/delete/${skuId}`)
      .catch((error) => handleResponseError(error));

    // API คืน {"status":"success","data":"success"}
    if (response.status >= 200 && response.status < 300 && response.data?.status === 'success') return;
    if (response.data?.status && response.data.status !== 'success') {
      // โยน body ทั้งก้อน (มี error_data เช่นรายการสินค้าที่ผูกสต็อกนี้อยู่)
      throw response.data;
    }
    if (response.status >= 200 && response.status < 300) return;
  } catch (error: any) {
    if (error?.error_data || error?.status === 'failed') throw error;
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('เกิดข้อผิดพลาดในการลบ SKU');
    }
  }
};

/**
 * ดึงข้อมูล SKU โดย ID
 * @param skuId - SKU ID
 * @returns Promise<SkuResponse>
 */
export const getSkuById = async (skuId: string): Promise<SkuResponse> => {
  try {
    const response: AxiosResponse<ApiResponse<SkuResponse>> = await httpClient
      .get(`/product/sku/${skuId}`)
      .catch((error) => handleResponseError(error));

    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'ไม่สามารถดึงข้อมูล SKU ได้');
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('เกิดข้อผิดพลาดในการดึงข้อมูล SKU');
    }
  }
};

/**
 * ดึงข้อมูล SKU ทั้งหมดของ Product
 * @param productId - Product ID
 * @returns Promise<SkuResponse[]>
 */
export const getSkusByProductId = async (productId: string): Promise<SkuResponse[]> => {
  try {
    const response: AxiosResponse<ApiResponse<SkuResponse[]>> = await httpClient
      .get(`/product/sku/product/${productId}`)
      .catch((error) => handleResponseError(error));

    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'ไม่สามารถดึงข้อมูล SKU ได้');
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('เกิดข้อผิดพลาดในการดึงข้อมูล SKU');
    }
  }
};

/**
 * ดึงข้อมูล SKU ทั้งหมดของ Variant
 * @param variantId - Variant ID
 * @returns Promise<SkuResponse[]>
 */
export const getSkusByVariantId = async (variantId: string): Promise<SkuResponse[]> => {
  try {
    // Note: This endpoint might need to be implemented in the backend
    // For now, we'll use the product endpoint and filter by variant
    const response: AxiosResponse<ApiResponse<SkuResponse[]>> = await httpClient
      .get(`/product/sku/variant/${variantId}`)
      .catch((error) => handleResponseError(error));

    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'ไม่สามารถดึงข้อมูล SKU ได้');
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('เกิดข้อผิดพลาดในการดึงข้อมูล SKU');
    }
  }
};

/**
 * ค้นหา SKU ตามรหัสสินค้า
 * @param productCode - รหัสสินค้า
 * @returns Promise<SkuResponse | null>
 */
export const findSkuByProductCode = async (productCode: string): Promise<SkuResponse | null> => {
  try {
    const response: AxiosResponse<ApiResponse<SkuResponse>> = await httpClient
      .get(`/product/sku/code/${productCode}`)
      .catch((error) => handleResponseError(error));

    if (response.data.success) return response.data.data;
    return null;
  } catch (error: any) {
    // If not found, return null instead of throwing error
    if (error.response?.status === 404) {
      return null;
    }
    throw new Error('เกิดข้อผิดพลาดในการค้นหา SKU');
  }
};

/**
 * ค้นหา SKU ตามชื่อ
 * @param skuName - ชื่อ SKU
 * @returns Promise<SkuResponse | null>
 */
export const findSkuByName = async (skuName: string): Promise<SkuResponse | null> => {
  try {
    const response: AxiosResponse<ApiResponse<SkuResponse>> = await httpClient
      .get(`/product/sku/name/${encodeURIComponent(skuName)}`)
      .catch((error) => handleResponseError(error));

    if (response.data.success) return response.data.data;
    return null;
  } catch (error: any) {
    // If not found, return null instead of throwing error
    if (error.response?.status === 404) {
      return null;
    }
    throw new Error('เกิดข้อผิดพลาดในการค้นหา SKU');
  }
};

/**
 * ตรวจสอบว่ารหัสสินค้าซ้ำหรือไม่
 * @param productCode - รหัสสินค้า
 * @param excludeSkuId - SKU ID ที่ต้องการยกเว้น (สำหรับการแก้ไข)
 * @returns Promise<boolean>
 */
export const isProductCodeDuplicate = async (
  productCode: string, 
  excludeSkuId?: string
): Promise<boolean> => {
  try {
    const existingSku = await findSkuByProductCode(productCode);
    if (!existingSku) {
      return false;
    }
    
    // If excludeSkuId is provided, check if it's the same SKU
    if (excludeSkuId && existingSku.id === excludeSkuId) {
      return false;
    }
    
    return true;
  } catch (error) {
    // If there's an error checking, assume it's not duplicate to allow the operation
    return false;
  }
};

/**
 * ตรวจสอบว่าชื่อ SKU ซ้ำหรือไม่
 * @param skuName - ชื่อ SKU
 * @param excludeSkuId - SKU ID ที่ต้องการยกเว้น (สำหรับการแก้ไข)
 * @returns Promise<boolean>
 */
export const isSkuNameDuplicate = async (
  skuName: string, 
  excludeSkuId?: string
): Promise<boolean> => {
  try {
    const existingSku = await findSkuByName(skuName);
    if (!existingSku) {
      return false;
    }
    
    // If excludeSkuId is provided, check if it's the same SKU
    if (excludeSkuId && existingSku.id === excludeSkuId) {
      return false;
    }
    
    return true;
  } catch (error) {
    // If there's an error checking, assume it's not duplicate to allow the operation
    return false;
  }
};

/**
 * บันทึก SKU หลายรายการพร้อมกัน
 * @param skus - ข้อมูล SKU ที่ต้องการบันทึก
 * @returns Promise<SkuResponse[]>
 */
export const createMultipleSkus = async (skus: CreateSkuRequest[]): Promise<SkuResponse[]> => {
  try {
    const promises = skus.map(sku => createSku(sku));
    const results = await Promise.all(promises);
    return results;
  } catch (error: any) {
    throw new Error('เกิดข้อผิดพลาดในการบันทึก SKU หลายรายการ');
  }
};

/**
 * อัปเดต SKU หลายรายการพร้อมกัน
 * @param updates - ข้อมูลการอัปเดต SKU
 * @returns Promise<SkuResponse[]>
 */
export const updateMultipleSkus = async (
  updates: { skuId: string; data: UpdateSkuRequest }[]
): Promise<SkuResponse[]> => {
  try {
    const promises = updates.map(({ skuId, data }) => updateSku(skuId, data));
    const results = await Promise.all(promises);
    return results;
  } catch (error: any) {
    throw new Error('เกิดข้อผิดพลาดในการอัปเดต SKU หลายรายการ');
  }
};

/**
 * ลบ SKU หลายรายการพร้อมกัน
 * @param skuIds - SKU IDs ที่ต้องการลบ
 * @returns Promise<void>
 */
export const deleteMultipleSkus = async (skuIds: string[]): Promise<void> => {
  try {
    const promises = skuIds.map(skuId => deleteSku(skuId));
    await Promise.all(promises);
  } catch (error: any) {
    throw new Error('เกิดข้อผิดพลาดในการลบ SKU หลายรายการ');
  }
};

/**
 * ดึงข้อมูล SKU พร้อม pagination
 * @param productId - Product ID
 * @param page - หน้าที่ต้องการ
 * @param pageSize - จำนวนรายการต่อหน้า
 * @returns Promise<{ skus: SkuResponse[]; total: number; page: number; pageSize: number }>
 */
export const getSkusWithPagination = async (
  productId: string,
  page: number = 1,
  pageSize: number = 10
): Promise<{ skus: SkuResponse[]; total: number; page: number; pageSize: number }> => {
  try {
    const response: AxiosResponse<ApiResponse<{
      skus: SkuResponse[];
      total: number;
      page: number;
      pageSize: number;
    }>> = await httpClient
      .get(`/product/sku/product/${productId}`, { params: { page, pageSize } })
      .catch((error) => handleResponseError(error));

    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'ไม่สามารถดึงข้อมูล SKU ได้');
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('เกิดข้อผิดพลาดในการดึงข้อมูล SKU');
    }
  }
};

/**
 * ค้นหา SKU ตามเงื่อนไข
 * @param searchParams - เงื่อนไขการค้นหา
 * @returns Promise<SkuResponse[]>
 */
export const searchSkus = async (searchParams: {
  productId?: string;
  variantId?: string;
  skuName?: string;
  productCode?: string;
  isActive?: boolean;
  page?: number;
  pageSize?: number;
}): Promise<SkuResponse[]> => {
  try {
    const response: AxiosResponse<ApiResponse<SkuResponse[]>> = await httpClient
      .get('/product/sku/search', { params: searchParams })
      .catch((error) => handleResponseError(error));

    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'ไม่สามารถค้นหา SKU ได้');
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('เกิดข้อผิดพลาดในการค้นหา SKU');
    }
  }
};

/**
 * ตรวจสอบสถานะการเชื่อมต่อ API
 * @returns Promise<boolean>
 */
export const checkApiHealth = async (): Promise<boolean> => {
  try {
    const response = await httpClient.get('/health');
    return response.status === 200;
  } catch (error) {
    return false;
  }
};

/**
 * ดึงข้อมูลสถิติ SKU
 * @param productId - Product ID
 * @returns Promise<{
 *   total: number;
 *   active: number;
 *   inactive: number;
 *   saved: number;
 *   unsaved: number;
 * }>
 */
export const getSkuStatistics = async (productId: string): Promise<{
  total: number;
  active: number;
  inactive: number;
  saved: number;
  unsaved: number;
}> => {
  try {
    const response: AxiosResponse<ApiResponse<{
      total: number;
      active: number;
      inactive: number;
      saved: number;
      unsaved: number;
    }>> = await httpClient
      .get(`/product/sku/statistics/${productId}`)
      .catch((error) => handleResponseError(error));

    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'ไม่สามารถดึงข้อมูลสถิติ SKU ได้');
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('เกิดข้อผิดพลาดในการดึงข้อมูลสถิติ SKU');
    }
  }
};

/**
 * อัปเดต SKU แบบ bulk update (variants และ prices)
 * @param data - ข้อมูล SKU ที่ต้องการอัปเดต
 * @returns Promise<UpdateProductSkuResponse>
 */
export const updateProductSku = async (
  input: UpdateProductSkuDto
): Promise<UpdateProductSkuResponse> => {
  const data: AxiosResponse<UpdateProductSkuResponse> = await httpClient
    .post(API.updateProductSku, input)
    .catch((error) => handleResponseError(error));
  return data.data;
};
